import dayjs, { Dayjs } from 'dayjs';

export class Utilities {
  public static formatTime = (date: Date) => {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12;
    minutes = minutes < 10 ? 0 + minutes : minutes;
    return `${hours}:${minutes} ${ampm}`;
  };
  public static getMatchingRecordTypeKey = (
    dealTypeSelected: string,
    recordType: Record<string, string>,
  ) => {
    const matchingKey = Object.keys(recordType).find((key) => recordType[key] === dealTypeSelected);
    return matchingKey || null;
  };
  public static parseDDMMYYYY = (dateStr: string): Date => {
    const parts = dateStr.split(/[\/\-\.]/);
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    console.warn(`Unable to parse date: ${dateStr}`);
    return new Date();
  };
  public static formatGMTOffset = (offsetHours: number): string => {
    const absHours = Math.abs(offsetHours);
    const sign = offsetHours >= 0 ? '+' : '-';

    if (offsetHours % 1 !== 0) {
      const hours = Math.floor(absHours);
      const minutes = Math.round((absHours % 1) * 60);
      return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
    }

    return `${sign}${absHours}`;
  };
  public static downloadCSV = (csvContent: any, fileName: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  public static formatDateAndAuthor = (item: { date: string; author: string }) => {
    return `${Utilities.formatDate(item.date)}${item.author ? ` by ${item.author}` : ''}`;
  };
  public static formatBytesToMB = (bytes: number): string => {
    const megabytes = bytes / 1000000;
    return megabytes > 0 && megabytes < 0.1 ? '0.1' : megabytes.toFixed(1);
  };
  public static removeFileExtension = (filename: string) => {
    const lastDot = filename.lastIndexOf('.');

    if (lastDot < 0) {
      return filename;
    }
    return filename.substring(0, lastDot);
  };
  public static getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0) return '';
    return filename.slice(lastDotIndex + 1).toLowerCase();
  };
  public static toCamelCase = (string: string) => {
    return string.split('')[0].toUpperCase() + string.slice(1);
  };
  public static capitalize = (stage: string): string => {
    if (!stage) return stage;
    return stage
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  public static camelCaseToTitleCase = (str: string): string => {
    if (!str) return '';

    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (match) => match.toUpperCase())
      .trim();
  };
  public static formatSnakeToTitle = (str: string) => {
    if (!str) return '';

    return str
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  public static formatDate = (dateString?: string | Dayjs) => {
    if (!dateString) return '';
    return dayjs(dateString).format('MM/DD/YY');
  };
  public static toLocaleStringUS = (value: number | string) => {
    return Number(value).toLocaleString('US');
  };
}

export const validators = {
  validateName: (value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue.length < 2) return false;
    return /^[a-zA-Z\s'-]+$/.test(trimmedValue);
  },

  validateCompany: (value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue.length < 3) return false;
    return /^[a-zA-Z0-9\s.,'-]+$/.test(trimmedValue);
  },

  validateEmail: (value: string) => {
    const trimmedValue = value.trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(trimmedValue);
  },

  validatePhone: (value: string) => {
    const trimmedValue = value.trim();
    const digitCount = trimmedValue.replace(/\D/g, '').length;
    if (digitCount < 7) return false;
    return /^[0-9\s-()+x]+$/.test(trimmedValue);
  },

  validateRoles: (value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) return false;
    return /^[a-zA-Z0-9\s,-]+$/.test(trimmedValue);
  },
  validateAccountName: (value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue.length < 2) return false;
    return /^[a-zA-Z\s'-]+$/.test(trimmedValue);
  },
};

export function trimOptionalStr(value: unknown): string | undefined {
  if (value == null) return undefined;
  const s = String(value).trim();
  return s === '' ? undefined : s;
}
